import {
  XLSX_TEMPLATE_TRANSLATION,
  type XlsxTranslationEntry,
} from './xlsx-template-translation.js';

/**
 * Translation table: Polish XLSX header -> DB field path, for the DOOH
 * inventory workbook (`szablon_DOOH.xlsx`).
 *
 * Analogous to NOSNIKI_HEADERS / XLSX_TEMPLATE_TRANSLATION for the OOH
 * template (`xlsx-template-translation.ts`) but a physically separate file:
 * the OOH and DOOH header sets are logically and physically separate
 * templates (dooh-import-template proposal, "Reuse the existing template
 * generator and adapter registry" decision).
 *
 * Source of truth for column names and order: dooh-import-template spec,
 * "DOOH template column contract" requirement.
 */

/**
 * Canonical ordered list of 35 headers for the DOOH inventory workbook
 * (`data/szablon_DOOH.xlsx`): identification (5), location (7), screen (3),
 * emission (7), costs (7), metadata (6).
 */
export const DOOH_NOSNIKI_HEADERS = [
  // ── 1. Identification ─────────────────────────────────────────
  'Numer Grupy RW',
  'Numer dostawcy',
  'Dostawca',
  'Typ nośnika',
  'Format',
  // ── 2. Location ───────────────────────────────────────────────
  'Województwo',
  'Aglomeracja',
  'Miejscowość',
  'Adres',
  'Opis lokalizacji',
  'Współrzędne - szerokość',
  'Współrzędne - długość',
  // ── 3. Screen ─────────────────────────────────────────────────
  'Liczba ekranów w lokalizacji',
  'Wielkość ekranu [cale]',
  'Outdoor',
  // ── 4. Emission ───────────────────────────────────────────────
  '[Digital] Czas jednej reklamy [s]',
  '[Digital] Ile reklam w pętli',
  '[Digital] Godziny pracy ekranu',
  '[Digital] Rozdzielczość ekranu',
  '[Digital] Dzienna liczba emisji',
  '[Digital] Liczba emisji total',
  '[Digital] Cena 1 spota',
  // ── 5. Costs ──────────────────────────────────────────────────
  'Czynsz',
  'Koszt ekspozycji',
  'Cena katalogowa',
  'Cena po rabacie',
  'RW na start',
  'Koszty dodatkowe',
  'Produkcja',
  // ── 6. Metadata ───────────────────────────────────────────────
  'Uwagi',
  'Tag 1',
  'Tag 2',
  'Tag 3',
  'Tag 4',
  'Tag 5',
] as const;

export type DoohXlsxTemplateTranslation = Record<
  (typeof DOOH_NOSNIKI_HEADERS)[number],
  XlsxTranslationEntry
>;

/**
 * Maps Polish header (required-suffix stripped) to { table, column }.
 *
 * Identification/location/cost headers reuse the exact same translation
 * targets as XLSX_TEMPLATE_TRANSLATION (same underlying DB fields as the
 * OOH template). Screen and [Digital] * headers map to the
 * media_unit_variants columns owned by the media-unit-variants capability.
 *
 * Tag 1..5 map to a placeholder table/column ('media_unit_tags.tag'), same
 * convention as the OOH translation table.
 */
export const DOOH_XLSX_TEMPLATE_TRANSLATION: DoohXlsxTemplateTranslation = {
  // ── 1. Identification ─────────────────────────────────────────
  'Numer Grupy RW': { table: 'media_units', column: 'grw_id' },
  'Numer dostawcy': { table: 'media_units', column: 'supplier_number' },
  Dostawca: { table: 'suppliers', column: 'name' },
  'Typ nośnika': { table: 'media_unit_variants', column: 'media_type' },
  Format: { table: 'media_formats', column: 'code' },
  // ── 2. Location ───────────────────────────────────────────────
  Województwo: { table: 'locations', column: 'region' },
  Aglomeracja: { table: 'locations', column: 'agglomeration' },
  Miejscowość: { table: 'media_units', column: 'city' },
  Adres: { table: 'locations', column: 'address' },
  'Opis lokalizacji': { table: 'media_units', column: 'location_description' },
  'Współrzędne - szerokość': { table: 'locations', column: 'latitude' },
  'Współrzędne - długość': { table: 'locations', column: 'longitude' },
  // ── 3. Screen ─────────────────────────────────────────────────
  'Liczba ekranów w lokalizacji': { table: 'media_unit_variants', column: 'screens_count' },
  'Wielkość ekranu [cale]': {
    table: 'media_unit_variants',
    column: 'digital_screen_size_inches',
  },
  Outdoor: { table: 'media_unit_variants', column: 'is_outdoor' },
  // ── 4. Emission ───────────────────────────────────────────────
  '[Digital] Czas jednej reklamy [s]': {
    table: 'media_unit_variants',
    column: 'digital_spot_duration_seconds',
  },
  '[Digital] Ile reklam w pętli': { table: 'media_unit_variants', column: 'digital_loop_size' },
  '[Digital] Godziny pracy ekranu': {
    table: 'media_unit_variants',
    column: 'digital_screen_hours',
  },
  '[Digital] Rozdzielczość ekranu': {
    table: 'media_unit_variants',
    column: 'digital_screen_resolution',
  },
  '[Digital] Dzienna liczba emisji': {
    table: 'media_unit_variants',
    column: 'digital_daily_emissions',
  },
  '[Digital] Liczba emisji total': {
    table: 'media_unit_variants',
    column: 'digital_total_campaign_emissions',
  },
  '[Digital] Cena 1 spota': { table: 'media_unit_variants', column: 'digital_spot_price' },
  // ── 5. Costs ──────────────────────────────────────────────────
  Czynsz: { table: 'media_unit_costs', column: 'rent' },
  'Koszt ekspozycji': { table: 'media_unit_costs', column: 'exposure_cost' },
  'Cena katalogowa': { table: 'media_unit_costs', column: 'catalog_price' },
  'Cena po rabacie': { table: 'media_unit_costs', column: 'discounted_price' },
  'RW na start': { table: 'media_unit_costs', column: 'starter_price' },
  'Koszty dodatkowe': { table: 'media_unit_costs', column: 'additional_costs' },
  Produkcja: { table: 'media_unit_costs', column: 'client_production' },
  // ── 6. Metadata ───────────────────────────────────────────────
  Uwagi: { table: 'media_units', column: 'notes' },
  // Placeholder mapping; all 5 columns merge into one tag list server-side.
  'Tag 1': { table: 'media_unit_tags', column: 'tag' },
  'Tag 2': { table: 'media_unit_tags', column: 'tag' },
  'Tag 3': { table: 'media_unit_tags', column: 'tag' },
  'Tag 4': { table: 'media_unit_tags', column: 'tag' },
  'Tag 5': { table: 'media_unit_tags', column: 'tag' },
};

/**
 * OOH + DOOH translation tables, merged. Used by applyColumnMapping
 * (validate-row.ts) since both template types set `mappingConfirmed = true`
 * at upload and never enter the manual mapping phase, so a dooh-typed job's
 * rows fall back to this merged lookup. Every header shared by both
 * templates maps to the same { table, column } target, so the merge never
 * produces a conflicting mapping.
 */
export const MERGED_XLSX_TEMPLATE_TRANSLATION = {
  ...XLSX_TEMPLATE_TRANSLATION,
  ...DOOH_XLSX_TEMPLATE_TRANSLATION,
};
